import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Loader2, AlertTriangle, Lightbulb, Map } from "lucide-react";
import { RemediationPanel } from "@/components/RemediationPanel";

const critBadge = {
  ALTA: "bg-destructive text-white",
  MEDIA: "bg-[#F59E0B] text-white",
  BASSA: "bg-[#00A859] text-white",
};
const levelBadge = {
  ALTA: "bg-destructive/10 text-destructive",
  ALTO: "bg-destructive/10 text-destructive",
  MEDIA: "bg-[#F59E0B]/10 text-[#B45309]",
  MEDIO: "bg-[#F59E0B]/10 text-[#B45309]",
  BASSA: "bg-[#00A859]/10 text-[#00784A]",
  BASSO: "bg-[#00A859]/10 text-[#00784A]",
};

export default function ProcessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const { canEdit } = useAuth();

  const load = async () => { const { data } = await api.get(`/processes/${id}`); setP(data); };
  useEffect(() => { load(); }, [id]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/processes/${id}/analyze`);
      setP((prev) => ({ ...prev, ai_analysis: data }));
      toast.success("Analisi processo completata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  if (!p) return <Layout><PageHeader title="Processo" /><div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div></Layout>;

  const ai = p.ai_analysis;

  return (
    <Layout>
      <PageHeader title={p.name} subtitle={p.category || "Processo"}>
        <Button variant="outline" className="rounded-none" onClick={() => navigate("/processes")}><ArrowLeft className="w-4 h-4 mr-2" /> Indietro</Button>
        {canEdit && (
          <Button data-testid="analyze-process-btn" className="rounded-none" onClick={analyze} disabled={analyzing}>
            {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Analizza processo
          </Button>
        )}
      </PageHeader>

      <div className="p-8 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-border p-6">
            <div className="data-label">Stato attuale (as-is)</div>
            <p className="text-sm mt-2">{p.description || "—"}</p>
          </div>
          <div className="bg-white border border-border p-6">
            <div className="data-label">Criticità note</div>
            <p className="text-sm mt-2">{p.pain_points || "—"}</p>
          </div>
        </div>

        <div className="bg-white border border-border p-6" data-testid="process-ai-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <h3 className="font-heading font-bold text-lg tracking-tight">Analisi & Ottimizzazione AI</h3>
            </div>
            {ai?.criticality_level && <span className={`px-3 py-1 text-xs font-bold tracking-wide ${critBadge[ai.criticality_level]}`} data-testid="process-criticality">Criticità {ai.criticality_level}</span>}
          </div>

          {!ai ? (
            <p className="text-sm text-muted-foreground">Nessuna analisi generata. Clicca "Analizza processo" per ottenere criticità, proposte di automazione e roadmap con priorità e impatto (Claude Sonnet 4.6).</p>
          ) : (
            <div className="space-y-8">
              <p className="text-sm">{ai.summary}</p>

              {ai.issues?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 data-label mb-2"><AlertTriangle className="w-3.5 h-3.5 text-destructive" /> Criticità rilevate</div>
                  <ul className="space-y-1 text-sm list-disc pl-4">{ai.issues.map((s, i) => <li key={i}>{s}</li>)}</ul>
                </div>
              )}

              {ai.optimizations?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 data-label mb-3"><Lightbulb className="w-3.5 h-3.5 text-primary" /> Proposte di ottimizzazione</div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {ai.optimizations.map((o, i) => (
                      <div key={i} className="border border-border p-4">
                        <div className="font-medium text-sm">{o.title}</div>
                        <p className="text-sm text-muted-foreground mt-1">{o.description}</p>
                        {o.suggested_tools?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {o.suggested_tools.map((t, j) => <span key={j} className="text-[10px] font-mono bg-secondary px-2 py-0.5">{t}</span>)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {ai.roadmap?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 data-label mb-3"><Map className="w-3.5 h-3.5 text-primary" /> Roadmap di miglioramento</div>
                  <div className="border border-border overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-border text-left">
                        <th className="data-label px-4 py-2">Fase</th>
                        <th className="data-label px-4 py-2">Azione</th>
                        <th className="data-label px-4 py-2">Priorità</th>
                        <th className="data-label px-4 py-2">Impatto</th>
                        <th className="data-label px-4 py-2">Effort</th>
                      </tr></thead>
                      <tbody>
                        {ai.roadmap.map((r, i) => (
                          <tr key={i} className={`border-b border-border ${i % 2 ? "bg-[#F9FAFB]" : "bg-white"}`}>
                            <td className="px-4 py-2 font-medium whitespace-nowrap">{r.phase}</td>
                            <td className="px-4 py-2">{r.action}</td>
                            <td className="px-4 py-2"><span className={`px-2 py-0.5 text-[10px] font-bold ${levelBadge[r.priority] || "bg-secondary"}`}>{r.priority}</span></td>
                            <td className="px-4 py-2"><span className={`px-2 py-0.5 text-[10px] font-bold ${levelBadge[r.impact] || "bg-secondary"}`}>{r.impact}</span></td>
                            <td className="px-4 py-2"><span className={`px-2 py-0.5 text-[10px] font-bold ${levelBadge[r.effort] || "bg-secondary"}`}>{r.effort}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <RemediationPanel process={p} canEdit={canEdit} onChange={load} />
      </div>
    </Layout>
  );
}
